import axios, { AxiosInstance, AxiosResponse } from 'axios';
import {
  UserCreate,
  UserOut,
  UserUpdate,
  LoginRequest,
  LoginResponse,
  PasswordResetConfirm,
  PasswordResetResponse,
  EventCreate,
  EventOut,
  EventBase,
  EventOccurrence,
  EventVenueCreate,
  EventVenueOut,
  EventVenueUpdate,
  EventVenueDuplicateOut,
  EventCloneRequest,
  EventCloneOut,
  VenueTypeOut,
  DrinkTypeCreate,
  DrinkTypeOut,
  DrinkTypeBase,
  DrinkCreate,
  DrinkOut,
  DrinkBase,
  FoodTypeCreate,
  FoodTypeOut,
  FoodCreate,
  FoodOut,
  CartItemCreate,
  CartOut,
  OrderCreate,
  OrderOut,
  OrderLogsOut,
  EventNightOut,
  EventNightSummaryOut,
  SectionCreate,
  SectionOut,
  SectionUpdate,
  TableCreate,
  TableOut,
  TableUpdate,
  BookingCreate,
  BookingOut,
  BookingUpdate,
  NotificationList,
  NotificationOut,
  StripePaymentConfig,
  CreatePaymentIntentRequest,
  CreatePaymentIntentResponse,
  GlobalSetting,
  GlobalSettingCreate,
  GlobalSettingUpdate,
  SystemSettingKeysResponse,
  GlobalSettingsSyncResponse,
  GlobalSettingsVerifyResponse,
  CountryTaxRate,
  CountryTaxRatesResponse,
  CountryTaxRateUpdate,
  BulkCountryTaxRatesUpdate,
  TaxRateKind,
  EventConfig,
  TranslatableEntityType,
  EntityTranslationsOut,
  EntityTranslationsUpdate,
  EventConfigsResponse,
  EventConfigSyncResponse,
  EventConfigVerifyResponse,
  BulkAssignVenueRequest,
  BulkAssignVenueResponse,
  ProductOut,
  ProductCreate,
  ProductUpdate,
  StockAdjustRequest,
  StockThresholdUpdate,
  CartaEntryOut,
  CartaEntryCreate,
  BulkImportResult,
  StockHistoryList,
  ComboRecipeOut,
  ComboRecipeUpsert,
  LocalOut,
  LocalCreate
} from '../types/api';
import { SECURITY_CONFIG, SecurityUtils, SecurityMiddleware } from '../config/security';
import i18n from '../i18n';

/**
 * Get Accept-Language header value from user's language preference
 * Maps app language codes to proper locale format for Accept-Language header
 */
function getAcceptLanguageHeader(): string {
  // Map app language codes to locale codes
  const localeMap: { [key: string]: string } = {
    'es': 'es-ES',
    'en': 'en-US',
    'fr': 'fr-FR',
    'pt': 'pt-BR'
  };

  // Get current language from i18n
  const currentLanguage = i18n.language || 'es';
  return localeMap[currentLanguage] || 'es-ES';
}

class ApiService {
  private api: AxiosInstance;
  private baseURL: string;

  constructor() {
    this.baseURL = SECURITY_CONFIG.API_URL;
    
    // Log API configuration in development
    if (import.meta.env.DEV) {
      console.log('🌐 API Service initialized with baseURL:', this.baseURL);
    }
    
    this.api = axios.create({
      baseURL: this.baseURL,
      headers: {
        ...SECURITY_CONFIG.SECURITY_HEADERS,
      },
      withCredentials: true, // Enable credentials for session persistence
    });

    // Request interceptor to add auth token and security headers
    this.api.interceptors.request.use(
      (config) => {
        // Add security headers
        config = SecurityMiddleware.addSecurityHeaders(config);
        
        // Add auth token
        const token = localStorage.getItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY);
        if (token && !SecurityUtils.isTokenExpired(token)) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // Add Accept-Language header from user's language preference
        // Backend uses this to determine locale for notifications
        config.headers['Accept-Language'] = getAcceptLanguageHeader();
        
        // Rate limiting check - only for authenticated endpoints to avoid interfering with anonymous access
        // Skip rate limiting for anonymous endpoints and public data endpoints
        const isAnonymousEndpoint = config.url?.includes('/anonymous/') || 
                                   config.url?.includes('/events/') && config.method === 'get' ||
                                   config.url?.includes('/drinks/') && config.url?.includes('event_id=') ||
                                   config.url?.includes('/foods/') && config.url?.includes('event_id=') ||
                                   config.url?.includes('/drink-types/') ||
                                   config.url?.includes('/foods/types/');
        
        if (token && !isAnonymousEndpoint) {
          const clientId = `user-${token.split('.')[0]}`;
          if (!SecurityUtils.rateLimit.checkLimit(
            clientId, 
            SECURITY_CONFIG.RATE_LIMIT.MAX_REQUESTS_PER_MINUTE, 
            60 * 1000
          )) {
            SecurityMiddleware.logSecurityEvent('Rate limit exceeded', { clientId });
            return Promise.reject(new Error('Rate limit exceeded. Please try again later.'));
          }
        }
        
        return config;
      },
      (error) => {
        SecurityMiddleware.logSecurityEvent('Request interceptor error', error);
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle token expiration and security validation
    this.api.interceptors.response.use(
      (response) => {
        // Check if response is HTML instead of JSON (common when API URL is wrong)
        const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
        if (contentType.includes('text/html')) {
          console.error('❌ API returned HTML instead of JSON. This usually means the API URL is incorrect.');
          console.error('Current API URL:', this.baseURL);
          console.error('Response preview:', response.data?.substring?.(0, 200) || response.data);
          return Promise.reject(new Error('API returned HTML instead of JSON. Please check API URL configuration.'));
        }
        
        // Validate response for security issues
        SecurityMiddleware.validateResponse(response);
        return response;
      },
      (error) => {
        SecurityMiddleware.logSecurityEvent('Response error', error);
        
        // Check if error response is HTML
        if (error.response?.data && typeof error.response.data === 'string' && error.response.data.trim().startsWith('<!DOCTYPE')) {
          console.error('❌ API error response is HTML. API URL might be incorrect:', this.baseURL);
          return Promise.reject(new Error('API returned HTML error. Please check API URL configuration.'));
        }
        
        if (error.response?.status === 401) {
          localStorage.removeItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY);
          localStorage.removeItem(SECURITY_CONFIG.USER_STORAGE_KEY);
          //window.location.href = '/login';
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Authentication
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response: AxiosResponse<LoginResponse> = await this.api.post('/auth/login-json', credentials);
    return response.data;
  }

  async register(userData: UserCreate): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.post('/auth/register', userData);
    return response.data;
  }

  // OAuth/SSO Authentication
  async loginWithGoogle(token: string): Promise<{ access_token: string; token_type: string; user: UserOut }> {
    const response = await this.api.post('/oauth/google/verify', {
      token,
      provider: 'google'
    });
    return response.data;
  }

  async loginWithMicrosoft(token: string): Promise<{ access_token: string; token_type: string; user: UserOut }> {
    const response = await this.api.post('/oauth/microsoft/verify', {
      token,
      provider: 'microsoft'
    });
    return response.data;
  }

  async loginWithApple(idToken: string, userData?: any): Promise<{ access_token: string; token_type: string; user: UserOut }> {
    const response = await this.api.post('/oauth/apple/verify', {
      token: idToken,
      provider: 'apple',
      user_data: userData
    });
    return response.data;
  }

  async getOAuthProviders(): Promise<{ providers: Record<string, boolean>; configured_providers: string[] }> {
    const response = await this.api.get('/oauth/providers');
    return response.data;
  }

  // SSO Terms and Conditions
  async verifyUserInfo(token: string, provider: string): Promise<{
    email: string;
    name?: string;
    given_name?: string;
    family_name?: string;
    picture?: string;
    provider: string;
  }> {
    const response = await this.api.post('/oauth/verify-user-info', {
      token,
      provider
    });
    return response.data;
  }

  async registerWithTerms(data: {
    user_info: {
      email: string;
      name?: string;
      given_name?: string;
      family_name?: string;
      picture?: string;
      provider: string;
    };
    terms_accepted: boolean;
  }): Promise<{ access_token: string; token_type: string; user: UserOut }> {
    const response = await this.api.post('/oauth/register-with-terms', data);
    return response.data;
  }

  async acceptTerms(termsAccepted: boolean): Promise<UserOut> {
    const response = await this.api.post('/oauth/accept-terms', {
      terms_accepted: termsAccepted
    });
    return response.data;
  }

  async forgotPassword(email: string): Promise<PasswordResetResponse> {
    const response: AxiosResponse<PasswordResetResponse> = await this.api.post('/auth/forgot-password', { email });
    return response.data;
  }

  async resetPassword(data: PasswordResetConfirm): Promise<PasswordResetResponse> {
    const response: AxiosResponse<PasswordResetResponse> = await this.api.post('/auth/reset-password', data);
    return response.data;
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword
    });
    return response.data;
  }

  async sendEmail(to: string, subject: string, htmlContent: string): Promise<{ message: string }> {
    const response: AxiosResponse<{ message: string }> = await this.api.post('/email/send', {
      to,
      subject,
      html_content: htmlContent
    });
    return response.data;
  }

  // Users
  async getUsers(params?: {
    skip?: number;
    limit?: number;
    event_id?: number;
    role?: string;
  }): Promise<UserOut[]> {
    const response: AxiosResponse<UserOut[]> = await this.api.get('/users/', { params });
    return response.data;
  }

  async getUser(userId: number): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.get(`/users/${userId}`);
    return response.data;
  }

  async createUser(userData: UserCreate): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.post('/users/', userData);
    return response.data;
  }

  async updateUser(userId: number, userData: UserUpdate): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.put(`/users/${userId}`, userData);
    return response.data;
  }

  async resetUserPassword(userId: number, newPassword: string): Promise<{ message: string }> {
    // Try POST endpoint first (most common pattern)
    try {
      const response: AxiosResponse<{ message: string }> = await this.api.post(`/users/${userId}/reset-password`, {
        new_password: newPassword
      });
      return response.data;
    } catch (error: any) {
      // If POST fails, try PATCH endpoint (alternative pattern)
      if (error.response?.status === 404 || error.response?.status === 405) {
        const response: AxiosResponse<{ message: string }> = await this.api.patch(`/users/${userId}/reset-password`, {
          new_password: newPassword
        });
        return response.data;
      }
      throw error;
    }
  }

  async deleteUser(userId: number): Promise<void> {
    await this.api.delete(`/users/${userId}`);
  }

  async getMyProfile(): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.get('/users/me/profile');
    return response.data;
  }

  async getUsersByEvent(eventId: number|undefined, params?: {
    skip?: number;
    limit?: number;
    role?: string;
  }): Promise<UserOut[]> {
    const response: AxiosResponse<UserOut[]> = await this.api.get(`/users/event/${eventId}/users`, { params });
    return response.data;
  }

  // Events
  async getEvents(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
    organizer_id?: number;
  }): Promise<EventOut[]> {
    const response: AxiosResponse<EventOut[]> = await this.api.get('/events/', { params });
    return response.data;
  }

  async createEvent(eventData: EventCreate): Promise<EventOut> {
    const response: AxiosResponse<EventOut> = await this.api.post('/events/', eventData);
    return response.data;
  }

  async uploadImage(file: File): Promise<{ url: string }> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response: AxiosResponse<{ url: string }> = await this.api.post(
      '/upload/image',
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async getEvent(eventId: number): Promise<EventOut> {
    const response: AxiosResponse<EventOut> = await this.api.get(`/events/${eventId}`);
    return response.data;
  }

  async getEventOccurrences(eventId: number, params: {
    start_date: string;
    end_date: string;
  }): Promise<EventOccurrence[]> {
    const normalizeDateParam = (value: string) => {
      const parsed = new Date(value);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().replace(/\.\d{3}Z$/, '');
      }
      return value.replace(/\.\d{3}Z$/, '');
    };
    const response: AxiosResponse<EventOccurrence[]> = await this.api.get(
      `/events/${eventId}/occurrences`,
      {
        params: {
          start_date: normalizeDateParam(params.start_date),
          end_date: normalizeDateParam(params.end_date),
        }
      }
    );
    return response.data;
  }

  // QR Code endpoints
  async getEventQRCode(eventId: number): Promise<{ qr_code: string }> {
    const response: AxiosResponse<{ qr_code: string }> = await this.api.get(`/events/${eventId}/qr-code`);
    return response.data;
  }

  async validateQRCode(qrData: string): Promise<{ event_id: number; valid: boolean }> {
    const response: AxiosResponse<{ event_id: number; valid: boolean }> = await this.api.get(`/events/qr/validate/${qrData}`);
    return response.data;
  }

  async updateEvent(eventId: number, eventData: EventBase): Promise<EventOut> {
    const response: AxiosResponse<EventOut> = await this.api.put(`/events/${eventId}`, eventData);
    return response.data;
  }

  async updateEventOrganizer(eventId: number, organizerId: number): Promise<EventOut> {
    const response: AxiosResponse<EventOut> = await this.api.patch(`/events/${eventId}/organizer`, {
      organizer_id: organizerId
    });
    return response.data;
  }

  async deleteEvent(eventId: number): Promise<void> {
    await this.api.delete(`/events/${eventId}`);
  }

  async toggleEventActive(eventId: number): Promise<void> {
    await this.api.patch(`/events/${eventId}/toggle-active`);
  }

  async getEventsByOrganizer(organizerId: number, params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
  }): Promise<EventOut[]> {
    const response: AxiosResponse<EventOut[]> = await this.api.get(`/events/organizer/${organizerId}`, { params });
    return response.data;
  }

  async getMyManagedEvents(params?: {
    skip?: number;
    limit?: number;
    active_only?: boolean;
  }): Promise<EventOut[]> {
    const response: AxiosResponse<EventOut[]> = await this.api.get('/events/my-events', { params });
    return response.data;
  }

  async getDeletedEvents(): Promise<EventOut[]> {
    const response: AxiosResponse<EventOut[]> = await this.api.get('/events/deleted');
    return response.data;
  }

  async restoreEvent(eventId: number): Promise<EventOut> {
    const response: AxiosResponse<EventOut> = await this.api.patch(`/events/${eventId}/restore`);
    return response.data;
  }

  // Event Venues
  async getEventVenues(eventId: number): Promise<EventVenueOut[]> {
    const response: AxiosResponse<EventVenueOut[]> = await this.api.get(`/events/${eventId}/venues`);
    return response.data;
  }

  async createEventVenue(eventId: number, venueData: EventVenueCreate): Promise<EventVenueOut> {
    const response: AxiosResponse<EventVenueOut> = await this.api.post(`/events/${eventId}/venues`, venueData);
    return response.data;
  }

  async updateEventVenue(eventId: number, venueId: number, venueData: EventVenueUpdate): Promise<EventVenueOut> {
    const response: AxiosResponse<EventVenueOut> = await this.api.patch(`/events/${eventId}/venues/${venueId}`, venueData);
    return response.data;
  }

  async deleteEventVenue(eventId: number, venueId: number): Promise<void> {
    await this.api.delete(`/events/${eventId}/venues/${venueId}`);
  }

  async getDeletedEventVenues(eventId: number): Promise<EventVenueOut[]> {
    const response: AxiosResponse<EventVenueOut[]> = await this.api.get(`/events/${eventId}/venues/deleted`);
    return response.data;
  }

  async restoreEventVenue(eventId: number, venueId: number): Promise<EventVenueOut> {
    const response: AxiosResponse<EventVenueOut> = await this.api.patch(`/events/${eventId}/venues/${venueId}/restore`);
    return response.data;
  }

  async duplicateEventVenue(venueId: number, targetEventId: number): Promise<EventVenueDuplicateOut> {
    const response: AxiosResponse<EventVenueDuplicateOut> = await this.api.post(
      `/events/venues/${venueId}/duplicate`,
      { target_event_id: targetEventId }
    );
    return response.data;
  }

  async cloneEvent(eventId: number, payload: EventCloneRequest): Promise<EventCloneOut> {
    const response: AxiosResponse<EventCloneOut> = await this.api.post(`/events/${eventId}/clone`, payload);
    return response.data;
  }

  // Drink Types
  async getDrinkTypes(params?: {
    skip?: number;
    limit?: number;
  }): Promise<DrinkTypeOut[]> {
    const response: AxiosResponse<DrinkTypeOut[]> = await this.api.get('/drink-types/', { params });
    return response.data;
  }

  async createDrinkType(drinkTypeData: DrinkTypeCreate): Promise<DrinkTypeOut> {
    const response: AxiosResponse<DrinkTypeOut> = await this.api.post('/drink-types/', drinkTypeData);
    return response.data;
  }

  async getDrinkType(typeId: number): Promise<DrinkTypeOut> {
    const response: AxiosResponse<DrinkTypeOut> = await this.api.get(`/drink-types/${typeId}`);
    return response.data;
  }

  async updateDrinkType(typeId: number, drinkTypeData: DrinkTypeBase): Promise<DrinkTypeOut> {
    const response: AxiosResponse<DrinkTypeOut> = await this.api.put(`/drink-types/${typeId}`, drinkTypeData);
    return response.data;
  }

  async deleteDrinkType(typeId: number): Promise<void> {
    await this.api.delete(`/drink-types/${typeId}`);
  }

  // Drinks
  async getDrinks(params?: {
    skip?: number;
    limit?: number;
    event_id?: number;
    event_venue_id?: number;
    type_id?: number;
  }): Promise<DrinkOut[]> {
    const response: AxiosResponse<DrinkOut[]> = await this.api.get('/drinks/', { params });
    return response.data;
  }

  // Every carta row for the filters, page by page: GET /drinks/ returns at
  // most `limit` rows (100 by default), so a single call drops the rest.
  async getAllDrinks(params?: { event_id?: number; event_venue_id?: number; type_id?: number }): Promise<DrinkOut[]> {
    const pageSize = 1000;
    const all: DrinkOut[] = [];
    for (let skip = 0; ; skip += pageSize) {
      const rows = await this.getDrinks({ ...params, skip, limit: pageSize });
      all.push(...rows);
      if (rows.length < pageSize) return all;
    }
  }

  async createDrink(drinkData: DrinkCreate): Promise<DrinkOut> {
    const response: AxiosResponse<DrinkOut> = await this.api.post('/drinks/', drinkData);
    return response.data;
  }

  async getDrink(drinkId: number): Promise<DrinkOut> {
    const response: AxiosResponse<DrinkOut> = await this.api.get(`/drinks/${drinkId}`);
    return response.data;
  }

  async updateDrink(drinkId: number, drinkData: DrinkBase): Promise<DrinkOut> {
    const response: AxiosResponse<DrinkOut> = await this.api.put(`/drinks/${drinkId}`, drinkData);
    return response.data;
  }

  async updateDrinkAvailability(drinkId: number, isAvailable: boolean): Promise<DrinkOut> {
    const response: AxiosResponse<DrinkOut> = await this.api.patch(`/drinks/${drinkId}/availability`, { is_available: isAvailable });
    return response.data;
  }

  async deleteDrink(drinkId: number): Promise<void> {
    await this.api.delete(`/drinks/${drinkId}`);
  }

  // Carta import template of an event: references the Local's catalog
  // products and the event's bars.
  async downloadDrinkTemplate(eventId: number): Promise<Blob> {
    const response = await this.api.get('/drinks/download-template', {
      params: { event_id: eventId },
      responseType: 'blob'
    });
    return response.data;
  }

  async bulkImportDrinks(file: File, eventId: number): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    
    const response = await this.api.post(`/drinks/bulk-import?event_id=${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Food Types
  async getFoodTypes(): Promise<FoodTypeOut[]> {
    const response: AxiosResponse<FoodTypeOut[]> = await this.api.get('/foods/types/');
    return response.data;
  }

  async createFoodType(foodTypeData: FoodTypeCreate): Promise<FoodTypeOut> {
    const response: AxiosResponse<FoodTypeOut> = await this.api.post('/foods/types/', foodTypeData);
    return response.data;
  }

  // Foods
  async getFoods(params?: {
    event_id?: number;
    event_venue_id?: number;
    type_id?: number;
  }): Promise<FoodOut[]> {
    const response: AxiosResponse<FoodOut[]> = await this.api.get('/foods/', { params });
    return response.data;
  }

  async createFood(foodData: FoodCreate): Promise<FoodOut> {
    const response: AxiosResponse<FoodOut> = await this.api.post('/foods/', foodData);
    return response.data;
  }

  async getFood(foodId: number): Promise<FoodOut> {
    const response: AxiosResponse<FoodOut> = await this.api.get(`/foods/${foodId}`);
    return response.data;
  }

  async updateFood(foodId: number, foodData: FoodCreate): Promise<FoodOut> {
    const response: AxiosResponse<FoodOut> = await this.api.put(`/foods/${foodId}`, foodData);
    return response.data;
  }

  async updateFoodAvailability(foodId: number, isAvailable: boolean): Promise<FoodOut> {
    const response: AxiosResponse<FoodOut> = await this.api.patch(`/foods/${foodId}/availability`, { is_available: isAvailable });
    return response.data;
  }

  async deleteFood(foodId: number): Promise<void> {
    await this.api.delete(`/foods/${foodId}`);
  }

  async downloadFoodTemplate(eventId: number): Promise<Blob> {
    const response = await this.api.get('/foods/download-template/', {
      params: { event_id: eventId },
      responseType: 'blob'
    });
    return response.data;
  }

  async bulkImportFoods(file: File, eventId: number): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post(`/foods/bulk-import/?event_id=${eventId}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  // Cart
  async getCart(eventId: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.get(`/cart/?event_id=${eventId}`);
    return response.data;
  }

  async addToCart(item: CartItemCreate, eventId: number): Promise<CartOut> {
    const requestBody = {
      ...item,
      event_id: eventId
    };
    const response: AxiosResponse<CartOut> = await this.api.post('/cart/add', requestBody);
    return response.data;
  }

  async updateCartItem(itemId: number, quantity: number, eventId: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.put(`/cart/update/${itemId}?event_id=${eventId}&quantity=${quantity}`);
    return response.data;
  }

  async removeFromCart(itemId: number, eventId: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.delete(`/cart/remove/${itemId}?event_id=${eventId}`);
    return response.data;
  }

  async clearCart(eventId: number): Promise<void> {
    await this.api.delete(`/cart/clear?event_id=${eventId}`);
  }

  async checkoutCart(eventId: number, orderStatus?: string, paymentMethod?: 'cash' | 'card'): Promise<OrderOut> {
    const params: any = { event_id: eventId };
    if (orderStatus) {
      params.order_status = orderStatus;
    }
    if (paymentMethod) {
      params.payment_method = paymentMethod;
    }
    const response: AxiosResponse<OrderOut> = await this.api.post(`/cart/checkout`, null, { params });
    return response.data;
  }

  // Anonymous Orders
  async getAnonymousCart(eventId: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.get(`/anonymous/cart?event_id=${eventId}`);
    return response.data;
  }

  async addToAnonymousCart(item: CartItemCreate, eventId: number): Promise<CartOut> {
    const requestBody = {
      ...item,
      event_id: eventId
    };
    const response: AxiosResponse<CartOut> = await this.api.post('/anonymous/cart/add', requestBody);
    return response.data;
  }

  async updateAnonymousCartItem(eventId: number, itemId: number, quantity: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.put(`/anonymous/cart/update/${itemId}?event_id=${eventId}&quantity=${quantity}`);
    return response.data;
  }

  async removeFromAnonymousCart(itemId: number, eventId: number): Promise<CartOut> {
    const response: AxiosResponse<CartOut> = await this.api.delete(`/anonymous/cart/remove/${itemId}?event_id=${eventId}`);
    return response.data;
  }

  async clearAnonymousCart(eventId: number): Promise<void> {
    await this.api.delete(`/anonymous/cart/clear?event_id=${eventId}`);
  }

  async checkoutAnonymousCart(eventId: number, cart: CartOut, orderStatus?: string, paymentMethod?: 'cash' | 'card'): Promise<OrderOut> {

    // Build the items array in the format expected by the backend
    const items = cart.items.map(item => ({
      drink_id: item.drink_id || null,
      food_id: item.food_id || null,
      quantity: item.quantity,
      event_id: eventId
    }));

    const requestBody: any = {
      items: items,
      event_id: eventId,
      customer_info: {} // Empty object for anonymous customers
    };

    if (orderStatus) {
      requestBody.order_status = orderStatus;
    }
    if (paymentMethod) {
      requestBody.payment_method = paymentMethod;
    }

    const response: AxiosResponse<OrderOut> = await this.api.post('/anonymous/checkout', requestBody);
    return response.data;
  }

  async getAnonymousOrder(orderId: number): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.get(`/anonymous/order/${orderId}`);
    return response.data;
  }

  async getAnonymousOrderByQr(qrCode: string, skipSecurity: boolean = false): Promise<OrderOut> {
    const params = skipSecurity ? { skip_security: 'true' } : {};
    const response: AxiosResponse<OrderOut> = await this.api.get(`/anonymous/order/qr/${qrCode}`, { params });
    return response.data;
  }


  async getAnonymousEventDrinks(eventId: number, options?: { excludeVip?: boolean }): Promise<DrinkOut[]> {
    const params = new URLSearchParams({ available_only: 'false' });
    if (options?.excludeVip) params.set('exclude_vip', 'true');
    const response: AxiosResponse<DrinkOut[]> = await this.api.get(`/anonymous/drinks/${eventId}?${params.toString()}`);
    return response.data;
  }

  async getAnonymousEventFoods(eventId: number, options?: { excludeVip?: boolean }): Promise<FoodOut[]> {
    const params = new URLSearchParams({ available_only: 'false' });
    if (options?.excludeVip) params.set('exclude_vip', 'true');
    const response: AxiosResponse<FoodOut[]> = await this.api.get(`/anonymous/foods/${eventId}?${params.toString()}`);
    return response.data;
  }

  async getAnonymousEvents(): Promise<EventOut[]> {
    const response: AxiosResponse<EventOut[]> = await this.api.get('/anonymous/events');
    return response.data;
  }

  async getAnonymousEventVenues(eventId: number): Promise<EventVenueOut[]> {
    const response: AxiosResponse<EventVenueOut[]> = await this.api.get(`/events/${eventId}/venues`);
    return response.data;
  }

  async getVenueTypes(): Promise<VenueTypeOut[]> {
    const response: AxiosResponse<VenueTypeOut[]> = await this.api.get('/venue-types/');
    return response.data;
  }

  async getAnonymousEventDrinksByVenue(eventId: number, venueId: number): Promise<DrinkOut[]> {
    const response: AxiosResponse<DrinkOut[]> = await this.api.get(`/drinks/?event_id=${eventId}&event_venue_id=${venueId}&available_only=false`);
    return response.data;
  }

  async getAnonymousEventFoodsByVenue(eventId: number, venueId: number): Promise<FoodOut[]> {
    const response: AxiosResponse<FoodOut[]> = await this.api.get(`/foods/?event_id=${eventId}&event_venue_id=${venueId}&available_only=false`);
    return response.data;
  }

  // Orders
  async getOrders(params?: {
    skip?: number;
    limit?: number;
    status?: string;
    event_id?: number;
    event_venue_id?: number;
    occurrence_start?: string;
    created_from?: string;
    created_to?: string;
  }): Promise<OrderOut[]> {
    const response: AxiosResponse<OrderOut[]> = await this.api.get('/orders/', { params });
    return response.data;
  }

  async getEventNights(eventId: number): Promise<EventNightOut[]> {
    const response: AxiosResponse<EventNightOut[]> = await this.api.get('/orders/nights', { params: { event_id: eventId } });
    return response.data;
  }

  async getEventNightSummary(eventId: number, occurrenceStart: string): Promise<EventNightSummaryOut> {
    const response: AxiosResponse<EventNightSummaryOut> = await this.api.get('/orders/nights/summary', {
      params: { event_id: eventId, occurrence_start: occurrenceStart },
    });
    return response.data;
  }

  async createOrder(orderData: OrderCreate): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.post('/orders/', orderData);
    return response.data;
  }

  async getOrder(orderId: number): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.get(`/orders/${orderId}`);
    return response.data;
  }

  async getOrderLogs(orderId: number): Promise<OrderLogsOut> {
    const response: AxiosResponse<OrderLogsOut> = await this.api.get(`/orders/${orderId}/logs`);
    return response.data;
  }

  // Bartender delivery workflow: queue of orders to hand over at the current
  // bar, and open (claim) / reject (release) / deliver a single order.
  async getBartenderQueue(params?: { skip?: number; limit?: number }): Promise<OrderOut[]> {
    const response: AxiosResponse<OrderOut[]> = await this.api.get('/orders/queue', { params });
    return response.data;
  }

  async claimOrder(orderId: number): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.post(`/orders/${orderId}/claim`);
    return response.data;
  }

  async releaseOrder(orderId: number): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.post(`/orders/${orderId}/release`);
    return response.data;
  }

  async deliverOrder(orderId: number): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.post(`/orders/${orderId}/deliver`);
    return response.data;
  }

  async updateOrderStatus(orderId: number, statusUpdate: string): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.put(`/orders/${orderId}?status_update=${statusUpdate}`);
    return response.data;
  }

  async deleteOrder(orderId: number): Promise<void> {
    await this.api.delete(`/orders/${orderId}`);
  }

  async getOrderByQr(qrCode: string): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.get(`/orders/qr/${qrCode}`);
    return response.data;
  }

  // Sections
  async createSection(sectionData: SectionCreate): Promise<SectionOut> {
    const response: AxiosResponse<SectionOut> = await this.api.post('/sections/', sectionData);
    return response.data;
  }

  async getSectionsByEvent(eventId: number): Promise<SectionOut[]> {
    const response: AxiosResponse<SectionOut[]> = await this.api.get(`/sections/event/${eventId}`);
    return response.data;
  }

  async getSection(sectionId: number): Promise<SectionOut> {
    const response: AxiosResponse<SectionOut> = await this.api.get(`/sections/${sectionId}`);
    return response.data;
  }

  async updateSection(sectionId: number, sectionData: SectionUpdate): Promise<SectionOut> {
    const response: AxiosResponse<SectionOut> = await this.api.put(`/sections/${sectionId}`, sectionData);
    return response.data;
  }

  async deleteSection(sectionId: number): Promise<void> {
    await this.api.delete(`/sections/${sectionId}`);
  }

  // Tables
  async createTable(tableData: TableCreate): Promise<TableOut> {
    const response: AxiosResponse<TableOut> = await this.api.post('/tables/', tableData);
    return response.data;
  }

  async getEventTables(eventId: number): Promise<TableOut[]> {
    const response: AxiosResponse<TableOut[]> = await this.api.get(`/tables/event/${eventId}`);
    return response.data;
  }

  async getSectionTables(sectionId: number): Promise<TableOut[]> {
    const response: AxiosResponse<TableOut[]> = await this.api.get(`/tables/section/${sectionId}`);
    return response.data;
  }

  async getTable(tableId: number): Promise<TableOut> {
    const response: AxiosResponse<TableOut> = await this.api.get(`/tables/${tableId}`);
    return response.data;
  }

  async updateTable(tableId: number, tableData: TableUpdate): Promise<TableOut> {
    const response: AxiosResponse<TableOut> = await this.api.put(`/tables/${tableId}`, tableData);
    return response.data;
  }

  async deleteTable(tableId: number): Promise<void> {
    await this.api.delete(`/tables/${tableId}`);
  }

  // Bookings
  async createBooking(bookingData: BookingCreate): Promise<BookingOut> {
    const response: AxiosResponse<BookingOut> = await this.api.post('/bookings/', bookingData);
    return response.data;
  }

  async getTableAvailability(eventId: number, params: {
    booking_date: string;
    start_time: string;
    end_time: string;
  }): Promise<any> {
    const response = await this.api.get(`/bookings/availability/${eventId}`, { params });
    return response.data;
  }

  async getMyBookings(): Promise<BookingOut[]> {
    const response: AxiosResponse<BookingOut[]> = await this.api.get('/bookings/my-bookings');
    return response.data;
  }

  async getEventBookings(eventId: number): Promise<BookingOut[]> {
    const response: AxiosResponse<BookingOut[]> = await this.api.get(`/bookings/event/${eventId}`);
    return response.data;
  }

  async getBooking(bookingId: number): Promise<BookingOut> {
    const response: AxiosResponse<BookingOut> = await this.api.get(`/bookings/${bookingId}`);
    return response.data;
  }

  async updateBooking(bookingId: number, bookingData: BookingUpdate): Promise<BookingOut> {
    const response: AxiosResponse<BookingOut> = await this.api.put(`/bookings/${bookingId}`, bookingData);
    return response.data;
  }

  async cancelBooking(bookingId: number): Promise<void> {
    await this.api.delete(`/bookings/${bookingId}`);
  }

  async getTableBookings(tableId: number, bookingDate: string): Promise<any> {
    const response = await this.api.get(`/bookings/table/${tableId}/bookings`, {
      params: { booking_date: bookingDate }
    });
    return response.data;
  }

  // Global Settings
  async getGlobalSettings(params?: {
    category?: string;
    is_active?: boolean;
  }): Promise<GlobalSetting[]> {
    const response: AxiosResponse<GlobalSetting[]> = await this.api.get('/global-settings/', { params });
    return response.data;
  }

  async getGlobalSetting(settingId: number): Promise<GlobalSetting> {
    const response: AxiosResponse<GlobalSetting> = await this.api.get(`/global-settings/${settingId}`);
    return response.data;
  }

  async getGlobalSettingByKey(settingKey: string): Promise<GlobalSetting> {
    const response: AxiosResponse<GlobalSetting> = await this.api.get(`/global-settings/key/${settingKey}`);
    return response.data;
  }

  async updateGlobalSetting(settingId: number, data: GlobalSettingUpdate): Promise<GlobalSetting> {
    const response: AxiosResponse<GlobalSetting> = await this.api.put(`/global-settings/${settingId}`, data);
    return response.data;
  }

  async updateGlobalSettingByKey(settingKey: string, data: GlobalSettingUpdate): Promise<GlobalSetting> {
    const response: AxiosResponse<GlobalSetting> = await this.api.put(`/global-settings/key/${settingKey}`, data);
    return response.data;
  }

  async getSystemSettingKeys(): Promise<SystemSettingKeysResponse> {
    const response: AxiosResponse<SystemSettingKeysResponse> = await this.api.get('/global-settings/system/keys');
    return response.data;
  }

  async syncGlobalSettings(): Promise<GlobalSettingsSyncResponse> {
    const response: AxiosResponse<GlobalSettingsSyncResponse> = await this.api.post('/global-settings/sync');
    return response.data;
  }

  async verifyGlobalSettings(): Promise<GlobalSettingsVerifyResponse> {
    const response: AxiosResponse<GlobalSettingsVerifyResponse> = await this.api.get('/global-settings/verify');
    return response.data;
  }

  // Country Tax Rates
  async getAllCountryTaxRates(): Promise<CountryTaxRatesResponse> {
    const response: AxiosResponse<CountryTaxRatesResponse> = await this.api.get('/global-settings/tax-rates/countries');
    return response.data;
  }

  async getCountryTaxRate(countryCode: string): Promise<CountryTaxRate> {
    const response: AxiosResponse<CountryTaxRate> = await this.api.get(`/global-settings/tax-rates/country/${countryCode}`);
    return response.data;
  }

  async updateCountryTaxRate(countryCode: string, taxRate: number, kind: TaxRateKind = 'food'): Promise<CountryTaxRate> {
    const response: AxiosResponse<CountryTaxRate> = await this.api.put(`/global-settings/tax-rates/country/${countryCode}`, {
      tax_rate: taxRate
    }, { params: { kind } });
    return response.data;
  }

  async bulkUpdateCountryTaxRates(taxRates: Record<string, number>, kind: TaxRateKind = 'food'): Promise<CountryTaxRatesResponse> {
    const response: AxiosResponse<CountryTaxRatesResponse> = await this.api.post('/global-settings/tax-rates/countries/bulk', {
      country_tax_rates: taxRates
    }, { params: { kind } });
    return response.data;
  }

  // Event Configuration
  async getEventConfigs(eventId: number): Promise<EventConfigsResponse> {
    const response: AxiosResponse<EventConfig[] | EventConfigsResponse> = await this.api.get(`/events/${eventId}/configs`);
    // Handle both array response and object response
    if (Array.isArray(response.data)) {
      return {
        configs: response.data,
        event_id: eventId
      };
    }
    return response.data;
  }

  async getEventConfig(eventId: number, configKey: string): Promise<EventConfig> {
    const response: AxiosResponse<EventConfig> = await this.api.get(`/events/${eventId}/config/${configKey}`);
    return response.data;
  }

  async updateEventConfig(eventId: number, configKey: string, configValue: string): Promise<EventConfig> {
    const response: AxiosResponse<EventConfig> = await this.api.put(`/events/${eventId}/config/${configKey}`, {
      config_value: configValue
    });
    return response.data;
  }

  async syncEventConfigs(eventId: number): Promise<EventConfigSyncResponse> {
    const response: AxiosResponse<EventConfigSyncResponse> = await this.api.post(`/events/${eventId}/configs/sync`);
    return response.data;
  }

  async verifyEventConfigs(eventId: number): Promise<EventConfigVerifyResponse> {
    const response: AxiosResponse<EventConfigVerifyResponse> = await this.api.get(`/events/${eventId}/configs/verify`);
    return response.data;
  }

  // Notifications
  async getNotifications(params?: {
    limit?: number;
    offset?: number;
    unread_only?: boolean;
    order_id?: number; // For anonymous order notifications
  }): Promise<NotificationList> {
    // For anonymous users (with order_id), don't send Authorization header
    // For authenticated users (no order_id), use default interceptor which adds Authorization header
    if (params?.order_id) {
      // Anonymous user - use axios directly without interceptor
      const response = await axios.get(
        `${this.baseURL}/notifications/`,
        {
          params: {
            limit: params.limit,
            offset: params.offset,
            unread_only: params.unread_only,
            order_id: params.order_id,
          },
          headers: {
            ...SECURITY_CONFIG.SECURITY_HEADERS,
            'Accept-Language': getAcceptLanguageHeader(),
          },
          withCredentials: true,
        }
      );
      return response.data;
    }
    // If no order_id, use default api instance (interceptor will add Authorization header)
    const response: AxiosResponse<NotificationList> = await this.api.get('/notifications/', { params });
    return response.data;
  }

  async getUnreadCount(): Promise<{ count: number }> {
    const response = await this.api.get('/notifications/unread-count');
    return response.data;
  }

  async markNotificationAsRead(notificationId: number): Promise<NotificationOut> {
    const response: AxiosResponse<NotificationOut> = await this.api.put(`/notifications/${notificationId}/read`);
    return response.data;
  }

  async markAllNotificationsAsRead(): Promise<void> {
    await this.api.put('/notifications/mark-all-read');
  }

  async deleteNotification(notificationId: number): Promise<void> {
    await this.api.delete(`/notifications/${notificationId}`);
  }

  async clearAllNotifications(): Promise<void> {
    await this.api.delete('/notifications/');
  }

  // Locals
  async getLocals(): Promise<LocalOut[]> {
    const response: AxiosResponse<LocalOut[]> = await this.api.get('/locals/');
    return response.data;
  }

  async createLocal(payload: LocalCreate): Promise<LocalOut> {
    const response: AxiosResponse<LocalOut> = await this.api.post('/locals/', payload);
    return response.data;
  }

  async updateLocal(localId: number, payload: LocalCreate): Promise<LocalOut> {
    const response: AxiosResponse<LocalOut> = await this.api.put(`/locals/${localId}`, payload);
    return response.data;
  }

  async getLocal(localId: number): Promise<LocalOut> {
    const response: AxiosResponse<LocalOut> = await this.api.get(`/locals/${localId}`);
    return response.data;
  }

  async getLocalVenues(localId: number): Promise<EventVenueOut[]> {
    const response: AxiosResponse<EventVenueOut[]> = await this.api.get(`/locals/${localId}/venues`);
    return response.data;
  }

  // Products / Stock
  async getProducts(localId: number): Promise<ProductOut[]> {
    const response: AxiosResponse<ProductOut[]> = await this.api.get('/products/', { params: { local_id: localId } });
    return response.data;
  }

  async createProduct(payload: ProductCreate): Promise<ProductOut> {
    const response: AxiosResponse<ProductOut> = await this.api.post('/products/', payload);
    return response.data;
  }

  async getProduct(productId: number): Promise<ProductOut> {
    const response: AxiosResponse<ProductOut> = await this.api.get(`/products/${productId}`);
    return response.data;
  }

  async updateProduct(productId: number, payload: ProductUpdate): Promise<ProductOut> {
    const response: AxiosResponse<ProductOut> = await this.api.put(`/products/${productId}`, payload);
    return response.data;
  }

  async deleteProduct(productId: number): Promise<void> {
    await this.api.delete(`/products/${productId}`);
  }

  async adjustProductStock(productId: number, payload: StockAdjustRequest): Promise<ProductOut> {
    const response: AxiosResponse<ProductOut> = await this.api.post(`/products/${productId}/stock/adjust`, payload);
    return response.data;
  }

  async updateProductStockThreshold(productId: number, payload: StockThresholdUpdate): Promise<ProductOut> {
    const response: AxiosResponse<ProductOut> = await this.api.put(`/products/${productId}/stock/threshold`, payload);
    return response.data;
  }

  async getProductCarta(productId: number): Promise<CartaEntryOut[]> {
    const response: AxiosResponse<CartaEntryOut[]> = await this.api.get(`/products/${productId}/carta`);
    return response.data;
  }

  async addProductToCarta(productId: number, payload: CartaEntryCreate): Promise<CartaEntryOut> {
    const response: AxiosResponse<CartaEntryOut> = await this.api.post(`/products/${productId}/carta`, payload);
    return response.data;
  }

  async downloadProductsTemplate(isDrink: boolean): Promise<Blob> {
    const response = await this.api.get('/products/download-template', {
      params: { is_drink: isDrink },
      responseType: 'blob'
    });
    return response.data;
  }

  async bulkImportProducts(file: File, localId: number, isDrink: boolean): Promise<BulkImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.api.post('/products/bulk-import', formData, {
      params: { local_id: localId, is_drink: isDrink },
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async getProductHistory(productId: number, params?: { limit?: number; offset?: number }): Promise<StockHistoryList> {
    const response: AxiosResponse<StockHistoryList> = await this.api.get(`/products/${productId}/history`, { params });
    return response.data;
  }

  async getLocalStockHistory(params: { local_id: number; limit?: number; offset?: number }): Promise<StockHistoryList> {
    const response: AxiosResponse<StockHistoryList> = await this.api.get('/products/history', { params });
    return response.data;
  }

  async getComboRecipes(eventId: number): Promise<ComboRecipeOut[]> {
    const response: AxiosResponse<ComboRecipeOut[]> = await this.api.get('/warehouse/recipes', { params: { event_id: eventId } });
    return response.data;
  }

  async upsertComboRecipe(payload: ComboRecipeUpsert): Promise<ComboRecipeOut> {
    const response: AxiosResponse<ComboRecipeOut> = await this.api.post('/warehouse/recipes', payload);
    return response.data;
  }

  async deleteComboRecipe(recipeId: number): Promise<void> {
    await this.api.delete(`/warehouse/recipes/${recipeId}`);
  }

  // Push Notifications
  async getVapidPublicKey(): Promise<{ public_key: string }> {
    const response = await this.api.get('/notifications/push/vapid-public-key');
    const data = response.data as any;
    // Backend may return either { public_key } or { publicKey }
    const public_key = data?.public_key ?? data?.publicKey;
    return { public_key };
  }

  async subscribeToPushNotifications(subscription: {
    endpoint: string;
    keys: {
      p256dh: string;
      auth: string;
    };
    order_id?: number; // For anonymous order notifications
  }): Promise<{ message: string }> {
    // For anonymous users (with order_id), don't send Authorization header
    // For authenticated users (no order_id), use default interceptor which adds Authorization header
    const config: any = {};
    if (subscription.order_id) {
      // Anonymous user - explicitly exclude Authorization header
      // Use axios directly without interceptor to avoid adding Authorization header
      const response = await axios.post(
        `${this.baseURL}/notifications/push/subscribe`,
        subscription,
        {
          headers: {
            ...SECURITY_CONFIG.SECURITY_HEADERS,
            'Accept-Language': getAcceptLanguageHeader(),
          },
          withCredentials: true,
        }
      );
      return response.data;
    }
    // If no order_id, use default api instance (interceptor will add Authorization header)
    const response = await this.api.post('/notifications/push/subscribe', subscription);
    return response.data;
  }

  async unsubscribeFromPushNotifications(subscription: {
    endpoint: string;
    order_id?: number; // Optional: For anonymous order notifications
  }): Promise<{ message: string }> {
    // For anonymous users (with order_id), don't send Authorization header
    // For authenticated users (no order_id), use default interceptor which adds Authorization header
    if (subscription.order_id) {
      // Anonymous user - use axios directly without interceptor to avoid adding Authorization header
      const response = await axios.post(
        `${this.baseURL}/notifications/push/unsubscribe`,
        subscription,
        {
          headers: {
            ...SECURITY_CONFIG.SECURITY_HEADERS,
            'Accept-Language': getAcceptLanguageHeader(),
          },
          withCredentials: true,
        }
      );
      return response.data;
    }
    // If no order_id, use default api instance (interceptor will add Authorization header)
    const response = await this.api.post('/notifications/push/unsubscribe', subscription);
    return response.data;
  }

  // Stripe Payments (Spain / EUR)
  async getPaymentsConfig(): Promise<StripePaymentConfig> {
    const response: AxiosResponse<StripePaymentConfig> = await this.api.get('/payments/config');
    return response.data;
  }

  async createPaymentIntent(data: CreatePaymentIntentRequest): Promise<CreatePaymentIntentResponse> {
    const response: AxiosResponse<CreatePaymentIntentResponse> = await this.api.post('/payments/create-payment-intent', data);
    return response.data;
  }

  async updatePaymentIntent(paymentIntentId: string, data: { amount: number }): Promise<CreatePaymentIntentResponse> {
    const response: AxiosResponse<CreatePaymentIntentResponse> = await this.api.post(
      `/payments/update-payment-intent/${paymentIntentId}`,
      data
    );
    return response.data;
  }

  async attachOrderToPaymentIntent(paymentIntentId: string, orderId: number): Promise<{ success: boolean; payment_intent_id: string }> {
    const response: AxiosResponse<{ success: boolean; payment_intent_id: string }> = await this.api.post(
      `/payments/attach-order/${paymentIntentId}`,
      { order_id: orderId }
    );
    return response.data;
  }

  async scanOrderByQr(qrCode: string): Promise<OrderOut> {
    const response: AxiosResponse<OrderOut> = await this.api.get(`/orders/scan/${qrCode}`);
    return response.data;
  }

  async setMyVenue(eventVenueId: number | null): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.patch('/users/me/venue', {
      event_venue_id: eventVenueId
    });
    return response.data;
  }

  async bulkAssignDrinksToVenue(payload: BulkAssignVenueRequest): Promise<BulkAssignVenueResponse> {
    const response: AxiosResponse<BulkAssignVenueResponse> = await this.api.post('/drinks/bulk-assign-venue', payload);
    return response.data;
  }

  async bulkAssignFoodsToVenue(payload: BulkAssignVenueRequest): Promise<BulkAssignVenueResponse> {
    const response: AxiosResponse<BulkAssignVenueResponse> = await this.api.post('/foods/bulk-assign-venue', payload);
    return response.data;
  }

  async setBarmanVenue(userId: number, eventVenueId: number | null): Promise<UserOut> {
    const response: AxiosResponse<UserOut> = await this.api.patch(`/users/${userId}/venue`, {
      event_venue_id: eventVenueId
    });
    return response.data;
  }

  // Translation editor
  async getEntityTranslations(entityType: TranslatableEntityType, entityId: number): Promise<EntityTranslationsOut> {
    const response: AxiosResponse<EntityTranslationsOut> = await this.api.get(`/translations/${entityType}/${entityId}`);
    return response.data;
  }

  async updateEntityTranslations(
    entityType: TranslatableEntityType,
    entityId: number,
    data: EntityTranslationsUpdate
  ): Promise<EntityTranslationsOut> {
    const response: AxiosResponse<EntityTranslationsOut> = await this.api.put(`/translations/${entityType}/${entityId}`, data);
    return response.data;
  }

  // Utility methods
  setAuthToken(token: string): void {
    localStorage.setItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY, token);
  }

  getAuthToken(): string | null {
    const token = localStorage.getItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY);
    if (token && SecurityUtils.isTokenExpired(token)) {
      this.removeAuthToken();
      return null;
    }
    return token;
  }

  removeAuthToken(): void {
    localStorage.removeItem(SECURITY_CONFIG.TOKEN_STORAGE_KEY);
  }

  isAuthenticated(): boolean {
    return !!this.getAuthToken();
  }
}

export const apiService = new ApiService();
export default apiService; 